import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { ModalPortal } from '../modal/modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../motion';
import { AppButton } from '../button/app-button';
import {
  ReturnedUnit,
  UnitLimitError,
  getReturnedUnits,
  restoreUnits,
} from '../../api/units-client';

/* The revival surface. Restoration reactivates the ORIGINAL unit record — same
 * id, same number, same building, same history — so every row here names the
 * real unit and its building rather than offering an abstract count.
 *
 * Capacity is the allowance's decision, not this panel's: the panel only stops
 * the user from SELECTING more than `remaining` so the server's UNIT_LIMIT is
 * never the first time they hear the number. The server still checks the whole
 * batch under the mint lock; a refusal restores nothing and is shown verbatim.
 *
 * A PANEL, not only a dialog, for the same reason StripeCardPanel is: the
 * buy-more dialog embeds it as its post-purchase step, and a dialog inside a
 * dialog is two scrims, two focus traps and two Escape handlers. The standalone
 * RestoreUnitsDialog below wraps it for the billing page. */

const Group = styled.div`
  margin-bottom: ${spacing.sm};
`;

const GroupName = styled.div`
  margin-bottom: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

const Row = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${({ disabled }) => (disabled ? palette.slateLight : palette.ink)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background: ${({ disabled }) =>
      disabled ? 'transparent' : palette.irisTintSoft};
  }
`;

const RowWhy = styled.span`
  margin-left: auto;
  font-size: ${typography.size.xs}px;
  color: ${palette.slateLight};
`;

const List = styled.div`
  max-height: 260px;
  margin-bottom: ${spacing.sm};
  padding: ${spacing.xs};
  overflow-y: auto;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

const Hint = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Refusal = styled.p`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

export interface RestoreUnitsPanelProps {
  // How many units the org can still activate (allowance remaining). Selecting
  // is capped here so the server's UNIT_LIMIT is the backstop, not the UX.
  remaining: number;
  onRestored: (restored: number) => void;
  // The way out that is NOT a restore — "Not now" on the billing page, "Create
  // fresh units instead" inside the buy-more dialog. Always rendered: revival
  // is an offer, never a toll gate.
  skipLabel: string;
  onSkip: () => void;
}

export const RestoreUnitsPanel: React.FC<RestoreUnitsPanelProps> = ({
  remaining,
  onRestored,
  skipLabel,
  onSkip,
}) => {
  const [units, setUnits] = useState<ReturnedUnit[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getReturnedUnits()
      .then(r => {
        if (alive) {
          setUnits(r.units);
        }
      })
      .catch(() => {
        if (alive) {
          setLoadError(true);
        }
      });

    return () => {
      alive = false;
    };
  }, []);

  const groups = useMemo(() => {
    const byBuilding = new Map<string, ReturnedUnit[]>();
    for (const u of units ?? []) {
      const list = byBuilding.get(u.building_name) ?? [];
      list.push(u);
      byBuilding.set(u.building_name, list);
    }

    return [...byBuilding.entries()];
  }, [units]);

  const atCap = selected.length >= remaining;

  const toggle = (id: number) =>
    setSelected(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );

  const restore = async () => {
    if (busy || selected.length === 0) {
      return;
    }

    setBusy(true);
    setRefusal(null);
    try {
      const result = await restoreUnits(selected);
      toast.success(
        `Restored ${result.restored} unit${result.restored === 1 ? '' : 's'}`
      );
      onRestored(result.restored);
    } catch (error) {
      // The server refused the WHOLE batch — nothing was restored. Its message
      // carries the real numbers, so it is what gets shown.
      setRefusal(
        error instanceof UnitLimitError
          ? error.message
          : error instanceof Error && error.message
          ? error.message
          : 'Could not restore the units. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {loadError && (
        <Refusal role="alert">
          We could not load your returned units. Close this and try again.
        </Refusal>
      )}
      {units && units.length > 0 && (
        <List role="group" aria-label="Previously returned units">
          {groups.map(([building, list]) => (
            <Group key={building}>
              <GroupName>{building}</GroupName>
              {list.map(u => {
                const blocked = !u.restorable;
                const capped = atCap && !selected.includes(u.id);
                const disabled = blocked || capped || busy;

                return (
                  <Row key={u.id} disabled={disabled}>
                    <input
                      type="checkbox"
                      checked={selected.includes(u.id)}
                      disabled={disabled}
                      onChange={() => toggle(u.id)}
                      aria-label={`Restore unit ${u.unit_number} in ${u.building_name}`}
                    />
                    Unit {u.unit_number}
                    {u.name ? ` — ${u.name}` : ''}
                    {blocked && (
                      <RowWhy>
                        {!u.building_active
                          ? 'building is deactivated'
                          : 'number now in use'}
                      </RowWhy>
                    )}
                  </Row>
                );
              })}
            </Group>
          ))}
        </List>
      )}
      {units && units.length === 0 && (
        <Hint>There are no returned units to restore.</Hint>
      )}
      <Hint>
        Restoring brings back the original unit — same number, same building,
        same history — and uses one unit of your plan, exactly like creating
        one.{' '}
        {Number.isFinite(remaining)
          ? `You can restore up to ${remaining} right now; buy more units to restore the rest later.`
          : ''}
      </Hint>
      {refusal && <Refusal role="alert">{refusal}</Refusal>}
      <Actions>
        <AppButton variant="ghost" disabled={busy} onClick={onSkip}>
          {skipLabel}
        </AppButton>
        <AppButton
          variant="primary"
          disabled={busy || selected.length === 0}
          onClick={restore}
        >
          {busy
            ? 'Restoring…'
            : `Restore ${selected.length || ''} unit${
                selected.length === 1 ? '' : 's'
              }`.replace('  ', ' ')}
        </AppButton>
      </Actions>
    </>
  );
};

/* Portaled to <body> like ConfirmDialog and BuyUnitsDialog: an animated
 * ancestor creates a transform containing block that would trap a fixed
 * overlay.
 *
 * `modalTop`, because this dialog is raised from INSIDE the buy-units dialog
 * (which sits at `modal`, 2100); at `overlay` (2000) it painted underneath the
 * surface that opened it. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modalTop};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  width: 100%;
  max-width: 460px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Message = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export interface RestoreUnitsDialogProps {
  open: boolean;
  remaining: number;
  onDismiss: () => void;
  onRestored: (restored: number) => void;
}

export const RestoreUnitsDialog: React.FC<RestoreUnitsDialogProps> = ({
  open,
  remaining,
  onDismiss,
  onRestored,
}) => {
  const dialogRef = useDialogFocus(open, onDismiss);
  const cardMotion = useOverlayMotion('modal');

  return (
    <Presence>
      {open ? (
    <ModalPortal key="restore-units-dialog">
    <Scrim {...scrimMotion} onClick={onDismiss}>
      <Card
        {...cardMotion}
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Restore returned units"
        onClick={e => e.stopPropagation()}
      >
        <Title>Restore returned units</Title>
        <Message>
          These units were returned earlier. They kept their numbers, their
          buildings and their history, and can be brought back exactly as they
          were.
        </Message>
        <RestoreUnitsPanel
          remaining={remaining}
          onRestored={onRestored}
          skipLabel="Not now"
          onSkip={onDismiss}
        />
      </Card>
    </Scrim>
    </ModalPortal>
      ) : null}
    </Presence>
  );
};
